import type { ComponentType } from 'react';

export interface PostMeta {
  slug: string;
  title: string;
  description: string;
  date: string; // ISO yyyy-mm-dd
  author?: string;
  readingTime?: string;
  tags?: string[];
}

type Frontmatter = Omit<PostMeta, 'slug'>;

// Frontmatter is exported from each .mdx via remark-mdx-frontmatter (eager, so
// the index + route generation have metadata without loading post bundles).
const metaModules = import.meta.glob<Frontmatter>('../content/blog/*.mdx', {
  eager: true,
  import: 'frontmatter',
});

// The post components themselves are loaded lazily (one chunk per post).
const componentModules = import.meta.glob<{ default: ComponentType }>(
  '../content/blog/*.mdx',
);

const slugFromPath = (p: string) => p.split('/').pop()!.replace(/\.mdx$/, '');

export const posts: PostMeta[] = Object.entries(metaModules)
  .map(([p, fm]) => ({ slug: slugFromPath(p), ...fm }))
  // Newest first, with slug as a stable tiebreaker so SSR and client agree.
  .sort((a, b) => b.date.localeCompare(a.date) || a.slug.localeCompare(b.slug));

export const postComponents: Record<string, () => Promise<{ default: ComponentType }>> =
  Object.fromEntries(
    Object.entries(componentModules).map(([p, imp]) => [slugFromPath(p), imp]),
  );

export const formatPostDate = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
